// Submenú "Cheques" del módulo Control Bancario.
//   Chequeras · Emisión · Cheques girados · Posfechados · Anulación/protesto
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, BookText, Banknote, CheckCircle2, Loader2, Pencil, Plus, SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import {
  Campo, Cheque, Chequera, CuentaMini, CxpFacturaPicker, CxpPendiente, ESTADO_CHEQUE, ESTADO_CHEQUE_CLS, Modal, Switch, cb, cbRpc,
  fieldCls, hoyISO, label, money,
} from './shared';

function useCuentas() {
  return useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
}
const chequesSel = '?select=*,cuenta:cuenta_id(alias,numero,moneda,banco:banco_id(nombre))&deleted_at=is.null';
const invalidar = (qc: ReturnType<typeof useQueryClient>) =>
  ['cb-cheques', 'cb-chequeras', 'cb-libro', 'cb-posicion'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
const Estado = ({ e }: { e: string }) => <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CHEQUE_CLS[e] ?? 'bg-muted'}`}>{label(ESTADO_CHEQUE, e)}</span>;
const cmoneda = (c: Cheque) => c.cuenta?.moneda ?? 'USD';

// ============================================================ CHEQUERAS =======
const chequeraVacia = { cuenta_id: '', descripcion: '', prefijo: '', numero_desde: 1, numero_hasta: 100, siguiente: 1, estado: 'activa', activo: true };

export function Chequeras() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: cuentas = [] } = useCuentas();
  const { data: rows = [], isFetching } = useQuery<(Chequera & { cuenta?: { alias: string | null; numero: string; banco?: { nombre: string } } })[]>({
    queryKey: ['cb-chequeras'],
    queryFn: () => cb.list('chequeras', '?select=*,cuenta:cuenta_id(alias,numero,banco:banco_id(nombre))&deleted_at=is.null&order=created_at.desc'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof chequeraVacia>(chequeraVacia);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof chequeraVacia>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (q?: Chequera) => {
    if (q) { setEditId(q.id); setForm({ cuenta_id: q.cuenta_id, descripcion: q.descripcion ?? '', prefijo: q.prefijo ?? '', numero_desde: q.numero_desde, numero_hasta: q.numero_hasta, siguiente: q.siguiente, estado: q.estado, activo: q.activo }); }
    else { setEditId(null); setForm({ ...chequeraVacia, cuenta_id: cuentas[0]?.id ?? '' }); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { ...form, descripcion: form.descripcion || null, prefijo: form.prefijo || null,
        numero_desde: Number(form.numero_desde), numero_hasta: Number(form.numero_hasta),
        siguiente: editId ? Number(form.siguiente) : Number(form.numero_desde) };
      return editId ? cb.update('chequeras', editId, body) : cb.create('chequeras', body);
    },
    onSuccess: () => { toast.success('Chequera guardada'); setAbierto(false); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => cb.remove('chequeras', id),
    onSuccess: () => { toast.success('Chequera eliminada'); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><BookText className="w-5 h-5" /> Chequeras</h1>
        <Button onClick={() => abrir()} disabled={cuentas.length === 0}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Talonarios ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">Rango</th><th className="py-2 pr-3 text-right">Siguiente</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin chequeras</td></tr>}
              {rows.map((q) => (
                <tr key={q.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{q.cuenta?.alias ?? q.cuenta?.numero ?? '—'}</td>
                  <td className="py-2 pr-3">{q.descripcion ?? '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{q.numero_desde} – {q.numero_hasta}</td>
                  <td className="py-2 pr-3 text-right font-mono">{q.siguiente}</td>
                  <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${q.estado === 'activa' ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>{q.estado}</span></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(q)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar('¿Eliminar esta chequera?', { peligro: true })) eliminar.mutate(q.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar chequera' : 'Nueva chequera'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Cuenta *">
              <select className={fieldCls} value={form.cuenta_id} onChange={(e) => set({ cuenta_id: e.target.value })} disabled={!!editId}>
                <option value="">— Selecciona —</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero}</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} /></Campo>
              <Campo label="Prefijo (opcional)"><input className={fieldCls} value={form.prefijo} onChange={(e) => set({ prefijo: e.target.value })} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Desde *"><input type="number" className={fieldCls} value={form.numero_desde} onChange={(e) => set({ numero_desde: Number(e.target.value) })} /></Campo>
              <Campo label="Hasta *"><input type="number" className={fieldCls} value={form.numero_hasta} onChange={(e) => set({ numero_hasta: Number(e.target.value) })} /></Campo>
              {editId && <Campo label="Siguiente"><input type="number" className={fieldCls} value={form.siguiente} onChange={(e) => set({ siguiente: Number(e.target.value) })} /></Campo>}
            </div>
            {editId && (
              <div className="flex items-center gap-4 border-t pt-3">
                <Campo label="Estado" className="flex-1">
                  <select className={fieldCls} value={form.estado} onChange={(e) => set({ estado: e.target.value })}>
                    <option value="activa">Activa</option><option value="agotada">Agotada</option><option value="anulada">Anulada</option>
                  </select>
                </Campo>
                <Switch checked={form.activo} onChange={(v) => set({ activo: v })} label="Activo" />
              </div>
            )}
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.cuenta_id || Number(form.numero_hasta) < Number(form.numero_desde)} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================ EMISIÓN =========
const emiVacio = { cuenta_id: '', chequera_id: '', numero: '', beneficiario: '', monto: 0, fecha_emision: hoyISO(), fecha_cobro: hoyISO(), concepto: '', documento_ref: '', ticket_buy_id: '' };

export function EmisionCheques() {
  const qc = useQueryClient();
  const { data: cuentas = [] } = useCuentas();
  const { data: chequeras = [] } = useQuery<Chequera[]>({
    queryKey: ['cb-chequeras'],
    queryFn: () => cb.list<Chequera>('chequeras', '?select=*&deleted_at=is.null&estado=eq.activa&order=created_at.desc'),
  });
  const { data: recientes = [] } = useQuery<Cheque[]>({
    queryKey: ['cb-cheques', 'recientes'],
    queryFn: () => cb.list<Cheque>('cheques', `${chequesSel}&order=created_at.desc&limit=15`),
  });

  const [form, setForm] = useState<typeof emiVacio>(emiVacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof emiVacio>) => setForm((f) => ({ ...f, ...patch }));
  const chequerasCta = chequeras.filter((q) => q.cuenta_id === form.cuenta_id);
  const chequeraSel = chequeras.find((q) => q.id === form.chequera_id);

  const abrir = () => { setForm({ ...emiVacio, cuenta_id: cuentas[0]?.id ?? '' }); setAbierto(true); };

  const emitir = useMutation({
    mutationFn: () => cbRpc('cb_emitir_cheque', {
      _cuenta_id: form.cuenta_id, _chequera_id: form.chequera_id || null,
      _numero: form.numero ? Number(form.numero) : null, _beneficiario: form.beneficiario, _monto: Number(form.monto),
      _fecha_emision: form.fecha_emision, _fecha_cobro: form.fecha_cobro || form.fecha_emision,
      _concepto: form.concepto || null, _documento_ref: form.documento_ref || null,
      _ticket_buy_id: form.ticket_buy_id || null,
    }),
    onSuccess: (r: any) => { toast.success(`Cheque N° ${r.numero} emitido`); setAbierto(false); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><SquarePen className="w-5 h-5" /> Emisión de cheques</h1>
        <Button onClick={abrir} disabled={cuentas.length === 0}><Plus className="w-4 h-4 mr-1" /> Emitir cheque</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Emitidos recientemente</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Emisión</th><th className="py-2 pr-3">Cobro</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th>
            </tr></thead>
            <tbody>
              {recientes.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cheques</td></tr>}
              {recientes.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{c.numero}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_emision}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_cobro}</td>
                  <td className="py-2 pr-3">{c.beneficiario}</td>
                  <td className="py-2 pr-3">{c.cuenta?.alias ?? c.cuenta?.numero}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.monto, cmoneda(c))}</td>
                  <td className="py-2 pr-3"><Estado e={c.estado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal wide title="Emitir cheque" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Factura CxP a pagar (opcional)">
              <CxpFacturaPicker value={form.ticket_buy_id || null} onPick={(f: CxpPendiente | null) => set(f
                ? { ticket_buy_id: f.ticket_id, beneficiario: f.proveedor ?? form.beneficiario, monto: f.saldo, documento_ref: f.serie ?? '' }
                : { ticket_buy_id: '' })} />
              {form.ticket_buy_id && <p className="text-xs text-estable">Al girar el cheque se registrará el abono en la factura CxP y bajará su saldo.</p>}
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Cuenta *">
                <select className={fieldCls} value={form.cuenta_id} onChange={(e) => set({ cuenta_id: e.target.value, chequera_id: '', numero: '' })}>
                  <option value="">— Selecciona —</option>
                  {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
                </select>
              </Campo>
              <Campo label="Chequera">
                <select className={fieldCls} value={form.chequera_id} onChange={(e) => set({ chequera_id: e.target.value, numero: '' })} disabled={!form.cuenta_id}>
                  <option value="">— Sin chequera (número manual) —</option>
                  {chequerasCta.map((q) => <option key={q.id} value={q.id}>{q.descripcion ?? `${q.numero_desde}-${q.numero_hasta}`} (sig. {q.siguiente})</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label={`Número${chequeraSel ? ` (auto: ${chequeraSel.siguiente})` : ' *'}`}>
                <input type="number" className={fieldCls} value={form.numero} onChange={(e) => set({ numero: e.target.value })} placeholder={chequeraSel ? String(chequeraSel.siguiente) : ''} />
              </Campo>
              <Campo label="Monto *"><input type="number" step="0.01" min="0" className={fieldCls} value={form.monto} onChange={(e) => set({ monto: Number(e.target.value) })} /></Campo>
              <Campo label="Documento ref."><input className={fieldCls} value={form.documento_ref} onChange={(e) => set({ documento_ref: e.target.value })} placeholder="Factura / OP" /></Campo>
            </div>
            <Campo label="Beneficiario *"><input className={fieldCls} value={form.beneficiario} onChange={(e) => set({ beneficiario: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha de emisión *"><input type="date" className={fieldCls} value={form.fecha_emision} onChange={(e) => set({ fecha_emision: e.target.value })} /></Campo>
              <Campo label="Fecha de cobro (posfechado si es futura)"><input type="date" className={fieldCls} value={form.fecha_cobro} onChange={(e) => set({ fecha_cobro: e.target.value })} /></Campo>
            </div>
            <Campo label="Concepto"><input className={fieldCls} value={form.concepto} onChange={(e) => set({ concepto: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={emitir.isPending} disabled={!form.cuenta_id || !form.beneficiario.trim() || Number(form.monto) <= 0 || (!form.chequera_id && !form.numero)} onClick={() => emitir.mutate()}>Emitir</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ================================================ LISTA GENÉRICA DE CHEQUES ===
function useCheques(filtro: string) {
  return useQuery<Cheque[]>({
    queryKey: ['cb-cheques', filtro],
    queryFn: () => cb.list<Cheque>('cheques', `${chequesSel}&${filtro}`),
  });
}

// ============================================================ GIRADOS =========
// Los cheques posfechados eran una pantalla aparte, pero son EL MISMO registro
// con la fecha de cobro en el futuro — así los trata ya la proyección de flujo
// de caja. Se fusionaron aquí como filtro: una pantalla, tres vistas.
// La ruta vieja /cheques-posfechados sigue funcionando y abre con el filtro
// puesto, para no romper los enlaces guardados de nadie.
type FiltroCheque = 'todos' | 'circulacion' | 'posfechados';

export function ChequesGirados({ filtroInicial = 'todos' }: { filtroInicial?: FiltroCheque } = {}) {
  const qc = useQueryClient();
  const hoy = hoyISO();
  const [filtro, setFiltro] = useState<FiltroCheque>(filtroInicial);
  const consulta = filtro === 'posfechados' ? `estado=eq.girado&fecha_cobro=gt.${hoy}&order=fecha_cobro`
                 : filtro === 'circulacion' ? `estado=eq.girado&fecha_cobro=lte.${hoy}&order=fecha_cobro`
                 : 'estado=eq.girado&order=fecha_cobro';
  const { data: rows = [], isFetching } = useCheques(consulta);
  const [cobrar, setCobrar] = useState<Cheque | null>(null);
  const [fecha, setFecha] = useState(hoyISO());

  const doCobrar = useMutation({
    mutationFn: () => cbRpc('cb_cheque_cobrar', { _cheque_id: cobrar!.id, _fecha: fecha }),
    onSuccess: () => { toast.success('Cheque cobrado'); setCobrar(null); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Días que faltan para la fecha de cobro; negativo = ya venció y sigue sin cobrarse.
  const dias = (f: string) => Math.ceil((new Date(f + 'T00:00:00').getTime() - new Date(hoy + 'T00:00:00').getTime()) / 86400000);
  const total = rows.reduce((a, c) => a + Number(c.monto || 0), 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Banknote className="w-5 h-5" /> Cheques girados</h1>
        <div className="flex gap-1 rounded-lg border p-1">
          {([['todos', 'Todos'], ['circulacion', 'En circulación'], ['posfechados', 'Posfechados']] as const).map(([k, t]) => (
            <button key={k} onClick={() => setFiltro(k)}
              className={`rounded-md px-3 py-1 text-sm ${filtro === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        {filtro === 'posfechados'
          ? 'Cheques girados con fecha de cobro futura: compromisos de pago que aún no salen de la cuenta.'
          : filtro === 'circulacion'
          ? 'Cheques ya vencidos o que se cobran hoy, y que siguen sin presentarse.'
          : 'Todos los cheques girados y no cobrados, vencidos y por vencer.'}
      </p>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between gap-2">
            <span>{rows.length} cheque(s)</span>
            <span className="font-mono tabular-nums text-sm font-normal text-muted-foreground">{money(total, rows[0] ? cmoneda(rows[0]) : 'USD')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Cobro</th>
              <th className="py-2 pr-3 text-right">Faltan</th>
              <th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Cuenta</th>
              <th className="py-2 pr-3 text-right">Monto</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin cheques en esta vista</td></tr>}
              {rows.map((c) => {
                const d = dias(c.fecha_cobro);
                return (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 pr-3 font-mono">{c.numero}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{c.fecha_cobro}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {d > 0
                        ? <span className="rounded-full bg-pendiente-soft text-pendiente px-2 py-0.5 text-xs">{d} día(s)</span>
                        : <span className="text-xs text-muted-foreground">{d === 0 ? 'hoy' : `vencido hace ${-d} d`}</span>}
                    </td>
                    <td className="py-2 pr-3">{c.beneficiario}</td>
                    <td className="py-2 pr-3">{c.cuenta?.alias ?? c.cuenta?.numero}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.monto, cmoneda(c))}</td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => { setFecha(hoyISO()); setCobrar(c); }}><CheckCircle2 className="w-4 h-4 mr-1" /> Cobrar</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {cobrar && (
        <Modal title={`Cobrar cheque N° ${cobrar.numero}`} onClose={() => setCobrar(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{cobrar.beneficiario} · {money(cobrar.monto, cmoneda(cobrar))}</p>
            <Campo label="Fecha de cobro"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setCobrar(null)}>Cancelar</Button>
              <Button isLoading={doCobrar.isPending} onClick={() => doCobrar.mutate()}>Confirmar cobro</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ======================================================= ANULACIÓN / PROTESTO =
export function AnulacionCheques() {
  const qc = useQueryClient();
  const { data: girados = [], isFetching } = useCheques('estado=eq.girado&order=numero');
  const { data: historial = [] } = useCheques('estado=in.(anulado,protestado)&order=updated_at.desc&limit=30');
  const [obj, setObj] = useState<{ c: Cheque; accion: 'anular' | 'protestar' } | null>(null);
  const [motivo, setMotivo] = useState('');

  const doAccion = useMutation({
    mutationFn: () => cbRpc('cb_cheque_anular', { _cheque_id: obj!.c.id, _motivo: motivo, _accion: obj!.accion }),
    onSuccess: () => { toast.success(obj!.accion === 'protestar' ? 'Cheque protestado' : 'Cheque anulado'); setObj(null); setMotivo(''); invalidar(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Ban className="w-5 h-5" /> Anulación y protesto de cheques</h1>
      <p className="text-sm text-muted-foreground">Anular (error/no entregado) o protestar (devuelto por el banco) un cheque girado. El débito del libro se reversa si no está en una conciliación cerrada.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cheques girados ({girados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {girados.length === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin cheques girados</td></tr>}
              {girados.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{c.numero}</td>
                  <td className="py-2 pr-3">{c.beneficiario}</td>
                  <td className="py-2 pr-3">{c.cuenta?.alias ?? c.cuenta?.numero}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.monto, cmoneda(c))}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => { setMotivo(''); setObj({ c, accion: 'anular' }); }}>Anular</Button>
                    <Button size="sm" variant="outline" className="ml-1" onClick={() => { setMotivo(''); setObj({ c, accion: 'protestar' }); }}>Protestar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Anulados / protestados ({historial.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Beneficiario</th><th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Motivo</th>
            </tr></thead>
            <tbody>
              {historial.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin registros</td></tr>}
              {historial.map((c) => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{c.numero}</td>
                  <td className="py-2 pr-3">{c.beneficiario}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(c.monto, cmoneda(c))}</td>
                  <td className="py-2 pr-3"><Estado e={c.estado} /></td>
                  <td className="py-2 pr-3 text-muted-foreground">{c.motivo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {obj && (
        <Modal title={`${obj.accion === 'protestar' ? 'Protestar' : 'Anular'} cheque N° ${obj.c.numero}`} onClose={() => setObj(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{obj.c.beneficiario} · {money(obj.c.monto, cmoneda(obj.c))}. Se reversará el débito en el libro.</p>
            <Campo label="Motivo *"><textarea className={`${fieldCls} h-20`} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setObj(null)}>Cancelar</Button>
              <Button isLoading={doAccion.isPending} disabled={!motivo.trim()} onClick={() => doAccion.mutate()}>Confirmar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
